/**
 * Popup-side helpers for the shell <-> OAuth callback popup handshake.
 *
 * Companion to `packages/ui-deployed-shell/src/features/drive-oauth-popup.ts`.
 * See that file's protocol comment for the full message-type contract;
 * the string constants below MUST stay in sync.
 *
 * Why this exists: in storage-partitioned embed contexts (Chrome 3PCD,
 * Safari ITP, Firefox ETP) the shell iframe and the top-level
 * `/oauth/callback` popup read from DIFFERENT localStorage partitions
 * even though they share an origin. The runtime auth token the shell
 * seeded into `oauth-access-token` is invisible to the popup, so the
 * popup's `fetchOAuthCallbackIntegration` / `exchangeOAuthCallbackCode`
 * calls go out without a bearer header, the server returns 403 /
 * VISITOR, and the callback page surfaces "Failed to load integration
 * configuration." into ITS partition -- where the shell can't see the
 * completion marker either. Result: a perceived infinite retry loop.
 *
 * postMessage is window-to-window, partition-immune, and same-origin
 * between the popup and the shell iframe. We use it to (a) request
 * the runtime auth context from the shell on popup mount, and (b)
 * signal completion / error back to the shell just before
 * `window.close()`.
 */

export const SB_OAUTH_CALLBACK_READY = "sb-oauth-callback-ready" as const;
export const SB_OAUTH_CALLBACK_TOKEN = "sb-oauth-callback-token" as const;
export const SB_OAUTH_CALLBACK_COMPLETE = "sb-oauth-callback-complete" as const;

export type ShellTokenReply = {
  token: string;
  profileKey?: string;
  integrationId: string;
};

export type RequestTokenFromShellArgs = {
  oneTimeCode: string;
  /**
   * How long to wait for the shell's reply before rejecting. Embed
   * callback flows require this postMessage token; they must not fall
   * back to a storage partition that may be invisible to the opener.
   */
  timeoutMs: number;
};

export type RequestTokenFromShellDeps = {
  /**
   * The `window.opener` reference, if any. `null` when the popup was
   * not opened by our shell or when COOP severed the opener relationship.
   */
  opener: Pick<Window, "postMessage"> | null;
  /** The popup's own window for attaching the `message` listener. */
  windowTarget: Pick<Window, "addEventListener" | "removeEventListener">;
  /**
   * Origin of this popup window. Incoming `SB_OAUTH_CALLBACK_TOKEN`
   * replies are accepted when `event.origin` matches this value or
   * `openerTargetOrigin` (cross-origin embed app opener).
   */
  popupOrigin: string;
  /**
   * Origin of `window.opener`. `SB_OAUTH_CALLBACK_READY` is posted
   * with this targetOrigin. Defaults to the popup origin when the
   * shell opened the popup; embed apps on another origin set
   * `openerOrigin` in OAuth state.
   */
  openerTargetOrigin: string;
  setTimeout: (cb: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
};

/** postMessage targetOrigin for messages sent to `window.opener`. */
export function resolveOAuthOpenerTargetOrigin(
  openerOrigin: string | undefined,
  popupOrigin: string,
): string {
  return openerOrigin ?? popupOrigin;
}

/**
 * Ask the shell for the runtime auth token + profile key needed to
 * authenticate the callback page's same-origin API calls. Resolves
 * rejects if there is no opener, the request times out, or every
 * reply was rejected (wrong origin, mismatched oneTimeCode). Legacy
 * localStorage handoff never calls this helper.
 */
export function requestTokenFromShell(
  args: RequestTokenFromShellArgs,
  deps: RequestTokenFromShellDeps,
): Promise<ShellTokenReply> {
  if (!deps.opener) {
    return Promise.reject(new Error("OAuth callback popup has no opener."));
  }
  const opener = deps.opener;

  return new Promise<ShellTokenReply>((resolve, reject) => {
    let settled = false;
    let timeoutHandle: unknown = undefined;
    let messageHandler: ((evt: MessageEvent) => void) | undefined;

    const cleanup = () => {
      if (timeoutHandle !== undefined) {
        deps.clearTimeout(timeoutHandle);
        timeoutHandle = undefined;
      }
      if (messageHandler) {
        deps.windowTarget.removeEventListener(
          "message",
          messageHandler as EventListener,
        );
        messageHandler = undefined;
      }
    };

    const settle = (value: ShellTokenReply) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(value);
    };

    const fail = (message: string) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error(message));
    };

    const isTrustedTokenReplyOrigin = (origin: string) =>
      origin === deps.popupOrigin || origin === deps.openerTargetOrigin;

    messageHandler = (evt: MessageEvent) => {
      if (!isTrustedTokenReplyOrigin(evt.origin)) return;
      const data = evt.data as
        | {
            type?: string;
            oneTimeCode?: string;
            token?: string;
            profileKey?: string;
            integrationId?: string;
          }
        | undefined;
      if (!data || typeof data !== "object") return;
      if (data.type !== SB_OAUTH_CALLBACK_TOKEN) return;
      if (data.oneTimeCode !== args.oneTimeCode) return;
      if (typeof data.token !== "string" || data.token.length === 0) return;
      settle({
        token: data.token,
        profileKey: data.profileKey,
        integrationId: data.integrationId ?? "",
      });
    };
    deps.windowTarget.addEventListener(
      "message",
      messageHandler as EventListener,
    );

    timeoutHandle = deps.setTimeout(
      () =>
        fail(
          "OAuth callback did not receive an authentication token from the opener.",
        ),
      args.timeoutMs,
    );

    // Posting AFTER the listener is attached prevents a synchronous
    // shell reply from firing before we'd see it.
    opener.postMessage(
      { type: SB_OAUTH_CALLBACK_READY, oneTimeCode: args.oneTimeCode },
      deps.openerTargetOrigin,
    );
  });
}

export type OAuthCallbackCompleteMessage = {
  type: typeof SB_OAUTH_CALLBACK_COMPLETE;
  oneTimeCode?: string;
  integrationId?: string;
  error?: string;
};

/**
 * Tell the opener (shell iframe or builder modal window) that the
 * callback flow has finished. Posted before `window.close()` so the
 * host can settle partition-immune without localStorage handoff keys.
 *
 * No-op when there is no opener (manually pasted OAuth URL).
 */
export function signalCompletionToShell(
  args: { oneTimeCode?: string; integrationId?: string; error?: string },
  deps: { opener: Pick<Window, "postMessage"> | null; targetOrigin: string },
): void {
  if (!deps.opener) return;
  if (!args.oneTimeCode && !args.integrationId) return;
  const payload: OAuthCallbackCompleteMessage = {
    type: SB_OAUTH_CALLBACK_COMPLETE,
  };
  if (args.oneTimeCode) payload.oneTimeCode = args.oneTimeCode;
  if (args.integrationId) payload.integrationId = args.integrationId;
  if (args.error) payload.error = args.error;
  deps.opener.postMessage(payload, deps.targetOrigin);
}

/** Returns true when `message` matches the pending OAuth attempt identity. */
export function oauthCallbackCompleteMessageMatches(
  message: OAuthCallbackCompleteMessage,
  pending: { oneTimeCode?: string; integrationId: string },
): boolean {
  if (message.oneTimeCode) {
    return message.oneTimeCode === pending.oneTimeCode;
  }
  if (message.integrationId) {
    return message.integrationId === pending.integrationId;
  }
  return false;
}

/**
 * Subscribe to `SB_OAUTH_CALLBACK_COMPLETE` from a popup this window opened.
 * Used by `OAuthRedirectLoginModal` instead of polling localStorage.
 */
export function listenForOAuthCallbackCompletion(
  args: {
    matchesPending: (message: OAuthCallbackCompleteMessage) => boolean;
    onComplete: (error?: string) => void;
  },
  deps: {
    targetOrigin: string;
    windowTarget?: Pick<Window, "addEventListener" | "removeEventListener">;
  },
): () => void {
  const windowTarget = deps.windowTarget ?? window;
  const handler = (evt: MessageEvent) => {
    if (evt.origin !== deps.targetOrigin) return;
    const data = evt.data as OAuthCallbackCompleteMessage | undefined;
    if (!data || typeof data !== "object") return;
    if (data.type !== SB_OAUTH_CALLBACK_COMPLETE) return;
    if (!args.matchesPending(data)) return;
    args.onComplete(data.error);
  };
  windowTarget.addEventListener("message", handler as EventListener);
  return () => {
    windowTarget.removeEventListener("message", handler as EventListener);
  };
}

const OAUTH_POPUP_CLOSED_POLL_MS = 500;

export type WaitForOAuthPopupCompletionArgs = {
  pending: { integrationId: string; oneTimeCode: string };
  openedWindow: Window | null | undefined;
  targetOrigin: string;
};

export type OpenOAuthPopupAndWaitForCompletionArgs = {
  pending: { integrationId: string; oneTimeCode: string };
  openPopup: () => Window | null | undefined;
  targetOrigin: string;
};

export type WaitForOAuthPopupCompletionDeps = {
  windowTarget?: Pick<Window, "addEventListener" | "removeEventListener">;
  setInterval?: (cb: () => void, ms: number) => unknown;
  clearInterval?: (handle: unknown) => void;
};

/**
 * Opener-side waiter for a popup opened by this window (dynamic form connect,
 * builder modal). Resolves when the callback posts
 * `SB_OAUTH_CALLBACK_COMPLETE` or when the popup closes without a signal.
 */
export function waitForOAuthPopupCompletion(
  args: WaitForOAuthPopupCompletionArgs,
  deps: WaitForOAuthPopupCompletionDeps = {},
): Promise<[boolean, string | undefined]> {
  if (!args.openedWindow) {
    return Promise.resolve([
      false,
      "Unable to open the authentication window. Please check your browser's pop-up blocker settings and try again.",
    ]);
  }

  return waitForOAuthPopupCompletionAfterOpen(
    {
      openPopup: () => args.openedWindow,
      pending: args.pending,
      targetOrigin: args.targetOrigin,
    },
    deps,
  );
}

/**
 * Opens the popup after subscribing for completion so fast IdP redirects cannot
 * post `SB_OAUTH_CALLBACK_COMPLETE` before the opener is listening.
 */
export function openOAuthPopupAndWaitForCompletion(
  args: OpenOAuthPopupAndWaitForCompletionArgs,
  deps: WaitForOAuthPopupCompletionDeps = {},
): Promise<[boolean, string | undefined]> {
  return waitForOAuthPopupCompletionAfterOpen(args, deps);
}

function waitForOAuthPopupCompletionAfterOpen(
  args: OpenOAuthPopupAndWaitForCompletionArgs,
  deps: WaitForOAuthPopupCompletionDeps,
): Promise<[boolean, string | undefined]> {
  const windowTarget = deps.windowTarget ?? window;
  const setIntervalFn =
    deps.setInterval ?? ((cb, ms) => window.setInterval(cb, ms));
  const clearIntervalFn =
    deps.clearInterval ?? ((handle) => window.clearInterval(handle as number));

  return new Promise<[boolean, string | undefined]>((resolve) => {
    let settled = false;
    let pollHandle: unknown;
    let popupClosedObservedAtLeastOnce = false;

    const finish = (result: [boolean, string | undefined]) => {
      if (settled) return;
      settled = true;
      windowTarget.removeEventListener(
        "message",
        messageHandler as EventListener,
      );
      if (pollHandle !== undefined) {
        clearIntervalFn(pollHandle);
        pollHandle = undefined;
      }
      resolve(result);
    };

    const formatError = (error: string) =>
      error.startsWith("Authentication failed:")
        ? error
        : `Authentication failed: ${error}`;

    const messageHandler = (evt: MessageEvent) => {
      if (evt.origin !== args.targetOrigin) return;
      const data = evt.data as OAuthCallbackCompleteMessage | undefined;
      if (!data || typeof data !== "object") return;
      if (data.type !== SB_OAUTH_CALLBACK_COMPLETE) return;
      if (!oauthCallbackCompleteMessageMatches(data, args.pending)) return;
      if (data.error) {
        finish([false, formatError(data.error)]);
      } else {
        finish([true, ""]);
      }
    };

    windowTarget.addEventListener("message", messageHandler as EventListener);

    const openedWindow = args.openPopup();
    if (!openedWindow) {
      finish([
        false,
        "Unable to open the authentication window. Please check your browser's pop-up blocker settings and try again.",
      ]);
      return;
    }
    if (settled) return;

    pollHandle = setIntervalFn(() => {
      if (!openedWindow?.closed) {
        popupClosedObservedAtLeastOnce = false;
        return;
      }
      if (!popupClosedObservedAtLeastOnce) {
        popupClosedObservedAtLeastOnce = true;
        return;
      }
      finish([
        false,
        "Authentication was cancelled or encountered an error. Please try again.",
      ]);
    }, OAUTH_POPUP_CLOSED_POLL_MS);
  });
}

/**
 * Report an authorization-code exchange failure back to the opener via
 * `notifyFinished` (postMessage when an opener exists).
 */
export function reportExchangeCodeFailure(
  message: string,
  deps: {
    notifyFinished: (error?: string) => void;
  },
): void {
  deps.notifyFinished(message);
}
